import { AxiosResponse } from 'axios';
import axios from '@/_utils/axios';
import TimelineDndContext from '@/components/Packaging/LayeredTimeline/TimelineDndContext';
import { FE2BEAssemblyTemplate } from '@/components/Packaging/utils';
import { LoadingStatuses } from '@/constants/constants';
import { UserDisplay } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectPackagingActiveHover, selectPackagingActiveTemplate, selectPackagingLoadingStatus } from '@/redux/selectors/packagingSelectors';
import { selectTimelineOverlays, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import { setModuleState } from '@/redux/slices/dataTableSlice';
import { AssemblyTemplateBERequest } from '@/redux/slices/packagingSlice.types';
import { getAssemblyTemplateObject, setToolboxOptions } from '@/redux/slices/packagingSlice';
import { clearAssemblyTemplate, setOverlays, setSegments } from '@/redux/slices/packagingTimelineSlice';
import { SegmentService as PlayerAPI } from '@accurate-player/accurate-player-segment';
import styled from '@emotion/styled';
import { Container, Grid, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionCreators } from 'redux-undo';
import ActiveHoverDisplay from '../Packaging/ActiveHoverDisplay';
import ComponentList from '../Packaging/ComponentList/ComponentList';
import InventoryAssignmentModal from '../Packaging/InventoryAssignmentModal/InventoryAssignmentModal';
import LayeredTimeline from '../Packaging/LayeredTimeline/LayeredTimeline';
import TimelineViewManager from '../Packaging/TimelineViewManager';
import ToolboxDrawer from '../Packaging/ToolboxDrawer';
import PreviewUrlLoader from '../Packaging/VideoPreview/PreviewUrlLoader';
import VideoPreview from '../Packaging/VideoPreview/VideoPreview';
import { useTemplateTimelineValidation } from '@/components/AssemblyTemplates/hooks/useTemplateTimelineValidation.hook';
import { LabelValue } from '../_common';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import PolarisIcon from '../_common/PolarisIcon';
import Spinner from '../_common/Spinner';
import StepperButton from '../_common/StepperButton';
import { useToast } from '../_common/Toast';
import AssemblyTemplateBreadcrumb from './AssemblyTemplateBreadcrumb';
import { EDIT_TEMPLATE_TOOLBOX_OPTIONS_LIST } from '@/components/Packaging/constants';

export enum PackagingViewTypes {
  TIMELINE = 'timeline',
  LIST = 'list'
}

const AssemblyTemplateEdit = () => {
  const dispatch = useAppDispatch();
  const [view, setView] = useState<PackagingViewTypes>(PackagingViewTypes.TIMELINE);
  const playerApiRef = useRef<PlayerAPI | null>(null);
  const activeTemplate = useAppSelector(selectPackagingActiveTemplate);
  const segments = useAppSelector(selectTimelineSegments);
  const overlays = useAppSelector(selectTimelineOverlays);
  const [videoPreviewKey, setVideoPreviewKey] = useState(0);
  const { id: assemblyTemplateId } = useParams<{ id: string }>();
  const { success, error } = useToast();
  const activeHover = useAppSelector(selectPackagingActiveHover);
  const loadingStatus = useAppSelector(selectPackagingLoadingStatus);
  const isLoadingStatusFulfilled = loadingStatus === LoadingStatuses.fulfilled;
  const isLoadingStatusSettled = isLoadingStatusFulfilled || loadingStatus === LoadingStatuses.rejected;
  const [isSubmitting, setIsSubmitting] = useState({ state: false, message: '' });

  useEffect(() => {
    dispatch(setToolboxOptions(EDIT_TEMPLATE_TOOLBOX_OPTIONS_LIST));
    dispatch(getAssemblyTemplateObject(assemblyTemplateId));
    dispatch(setModuleState('packaging')); // This is needed to store titles search history in user preferences under the "packaging" namespace

    return () => {
      dispatch(setToolboxOptions([]));
      dispatch(clearAssemblyTemplate());
      dispatch(setModuleState(null));
    };
  }, []);

  useEffect(() => {
    if (activeTemplate) {
      dispatch(setSegments(activeTemplate.data.segments));
      dispatch(setOverlays(activeTemplate.data.overlays));
      dispatch(ActionCreators.clearHistory()); // reset history after all data is loaded and commited to the store
    }
  }, [activeTemplate]);

  useEffect(() => {
    setVideoPreviewKey((state) => state + 1);
  }, [segments]);

  const toggleView = (view: PackagingViewTypes) => {
    setView(view);
  };

  const { isAssemblyTemplateUpdateButtonDisabled, updateTemplateButtonTooltip } = useTemplateTimelineValidation({
    segments,
    isLoadingStatusFulfilled,
    isSubmitting: isSubmitting.state
  });

  const updateAssemblyTemplate = () => {
    setIsSubmitting({ state: true, message: 'Submitting...' });

    if (!activeTemplate) {
      throw new Error('`activeTemplate` is null');
    } else {
      console.log('activeTemplate', activeTemplate);
    }

    const payload: AssemblyTemplateBERequest = FE2BEAssemblyTemplate({
      ...activeTemplate,
      data: {
        segments: segments,
        overlays: overlays
      }
    });

    axios
      .post<unknown, AxiosResponse<unknown>, AssemblyTemplateBERequest>(`/assembly_templates/${assemblyTemplateId}/`, payload)
      .then(() => {
        success('Template updated successfully');
      })
      .catch(() => {
        error('Failed to update template');
      })
      .finally(() => {
        setIsSubmitting({ state: false, message: '' });
      });
  };

  return (
    <div>
      {!isLoadingStatusSettled && <StyledSpinner size={200} contained label="Loading Template..." />}
      {isSubmitting.state && <StyledSpinner size={200} contained label={isSubmitting.message} />}

      <PackagingHeaderContainer>
        <Grid container justifyContent="space-between" alignItems="center">
          <AssemblyTemplateBreadcrumb />
          <Grid item>
            <TemplateControls container justifyContent="flex-end" alignItems="center">
              <Typography variant="h4">
                Template: {activeTemplate ? activeTemplate.name || '-' : <PolarisIcon icon="loading_dots" height={30} style={{ marginRight: 10 }} />}
              </Typography>
            </TemplateControls>
          </Grid>
        </Grid>
      </PackagingHeaderContainer>

      <TimelineViewManager toggleView={toggleView}>
        <LabelValueWrapper>
          <LabelValue label="Template ID">{activeTemplate?.id || '-'}</LabelValue>
          <LabelValue label="Template Name">{activeTemplate?.name || '-'}</LabelValue>
          <LabelValue label="Created at">
            <DateTimeDisplay missingText="-" singleLine>
              {activeTemplate?.created_at || ''}
            </DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Created by">
            <UserDisplay user={activeTemplate?.created_by || null} />
          </LabelValue>
          <LabelValue label="Slug">{activeTemplate?.slug || '-'}</LabelValue>
          <LabelValue label="Template Type">{activeTemplate?.template_type || '-'}</LabelValue>
          <LabelValue label="Updated at">
            <DateTimeDisplay missingText="-" singleLine>
              {activeTemplate?.updated_at || ''}
            </DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Updated by">
            <UserDisplay user={activeTemplate?.updated_by || null} />
          </LabelValue>
        </LabelValueWrapper>
      </TimelineViewManager>

      <PreviewUrlLoader>
        {(isLoading, retryLoadingFailedPreview) => (
          <VideoPreview
            key={videoPreviewKey}
            playerApiRef={playerApiRef}
            isLoadingPreviewURLs={isLoading}
            retryLoadingFailedPreview={retryLoadingFailedPreview}
          />
        )}
      </PreviewUrlLoader>
      {activeHover && <ActiveHoverDisplay data={activeHover} />}

      {view === PackagingViewTypes.TIMELINE ? (
        <TimelineDndContext>
          {({ isDraggingNewSegment, isDraggingNewOverlay }) => (
            <>
              <LayeredTimeline
                playerApiRef={playerApiRef}
                isDraggingNewSegment={isDraggingNewSegment}
                isDraggingNewOverlay={isDraggingNewOverlay}
                isEditingTemplate
              />
              <ToolboxDrawer />
              <StepperButton
                complete={{
                  label: 'Update Template',
                  disabled: isAssemblyTemplateUpdateButtonDisabled,
                  tooltip: updateTemplateButtonTooltip,
                  onClick: updateAssemblyTemplate
                }}
              />
            </>
          )}
        </TimelineDndContext>
      ) : (
        <ComponentList />
      )}
      <div style={{ height: 145 }} />

      <InventoryAssignmentModal />
    </div>
  );
};

export default AssemblyTemplateEdit;

const PackagingHeaderContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing(3)} 0;
`;

const TemplateControls = styled(Grid)`
  .MuiTypography-h4 {
    font-weight: normal;
    opacity: 0.5;
    font-size: 1.3rem;
  }
`;

const StyledSpinner = styled(Spinner)`
  z-index: 9999;
`;

const LabelValueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;
